import React, { useState, useEffect } from 'react';
import { Row, Container, Col } from "react-bootstrap";
import { useLocation } from 'react-router-dom';
import { Button } from '@material-ui/core';
import AccessibilityPolicy from '../Disclosure/AccessibilityPolicy';
import PrivacyPolicy from '../Disclosure/PrivacyPolicy';
import TermsAndConditions from '../Disclosure/TermsAndConditions';
import StateDisclosure from '../Disclosure/StateDisclosure';
import SupportModal from '../Layout/SupportModal';
import FooterModalPopup from './FooterModalPopup';
import { getSessionStorageItem, SESSION_STORAGE_ACCOUNT_KEY } from '../../sessionStorage';
import './Footer.scss';

const links = {
  Terms: "TermsAndConditions",
  Security: "SecurityAndPrivacyPolicy",
  Accessibility: "AccessibilityPolicy",
  Contact: "ContactUs",
  StateLicense: "StateLicenseDisclosure",
};

const sentryBuildNumber = process.env.REACT_APP_SENTRY_BUILD_NUMBER;

export default function Footer({ expanded, isSignWithoutAuth }) {
  const location = useLocation();
  const year = new Date().getFullYear();
  const isLandingPage = location.pathname.toLowerCase().indexOf('landing') > -1;
  const isSinglePremiumWholeLife = location.pathname.toLowerCase().indexOf('single-premium-whole-life') > -1;

  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [disclaimer, setDisclaimer] = useState('');
  const [disclaimerBelow, setDisclaimerBelow] = useState(false);
  const [renderComponent, setRenderComponent] = useState(null);

  useEffect(() => {
    const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
    if (accountObj && accountObj.account && accountObj.account.accountId) {
      setAccountId(accountObj.account.accountId);
      setDisclaimer(accountObj.account.disclaimerHtml);
      setDisclaimerBelow(accountObj.account.customDisclaimerBelow);
    }
  }, []);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const onButtonClick = (linkName) => {
    if (linkName === links.Contact) {
      handleOpenModal();
    } else if (linkName === links.Terms) {
      setRenderComponent(<TermsAndConditions />);
      setInfoDialogOpen(true);
    } else if (linkName === links.Security) {
      setRenderComponent(<PrivacyPolicy />);
      setInfoDialogOpen(true);
    } else if (linkName === links.Accessibility) {
      setRenderComponent(<AccessibilityPolicy />);
      setInfoDialogOpen(true);
    } else if (linkName === links.StateLicense) {
      setRenderComponent(<StateDisclosure />);
      setInfoDialogOpen(true);
    }
  };

  const getDisclaimers = () => {
    return (
      <small>
        {disclaimerBelow && (
          <span>
            {covrDisclaimer()}
            <br />
          </span>
        )}
        {!isLandingPage && disclaimer && (
          <span>
            <span dangerouslySetInnerHTML={{ __html: disclaimer }} />
            <br />
          </span>
        )}
        {!disclaimerBelow && <span>{covrDisclaimer()}</span>}
      </small>
    );
  };

  const covrDisclaimer = () => {
    return (
      <span>
        {isLandingPage && <h5><b>Internal Use Only</b></h5>}
        {isSinglePremiumWholeLife && (
          <p>Secure Legacy is not available in New York. Insurance products are issued by Minnesota Life Insurance Company or Securian Life Insurance Company, a New York authorized insurer. Minnesota Life is not an authorized New York insurer and does not do insurance business in New York. Both companies are headquartered in Saint Paul, MN. Product availability and features may vary by state. Each insurer is solely responsible for the financial obligations under the policies or contracts it issues. 400 Robert Street North, St. Paul, MN 55101-2098.</p>
        )}
        <span>Insurance products are sold through Covr Financial Technologies Inc ("Covr"). Covr sells insurance as an agent or broker by contractual agreements with its insurance carrier providers. Covr is licensed in the following states: Delaware (3000067942), Idaho (607086), California (0L77229). All other state license numbers are available on our <Button variant="text" className={`${isLandingPage && 'link-color'}`} onClick={() => onButtonClick(links.StateLicense)}>State License Disclosure Page</Button>.
        </span>
      </span>
    );
  };

  return (
    <footer className={`${isSignWithoutAuth && 'ml-0'} footer py-3 flex-fill ${expanded ? "sidebar-open" : "sidebar-close"}`}>
      <Container fluid>
        <Col lg={12}>
          <Row className="justify-content-center">
            <Col lg={isLandingPage ? 11 : 6}>{getDisclaimers()}</Col>
          </Row>
          <Row className={`justify-content-center my-4 links`}>
            {!isLandingPage && (
              <Col xs={12} sm className="d-flex align-items-center justify-content-center">
                <Button variant="text" onClick={() => onButtonClick(links.Terms)}>Terms &#38; Conditions</Button>
              </Col>
            )}
            <Col xs={12} sm lg={isLandingPage && 3} className={`d-flex align-items-center justify-content-center ${isLandingPage && 'pr-0'}`}>
              <Button variant="text" className={`${isLandingPage && 'link-color'}`} onClick={() => onButtonClick(links.Security)}>Security &#38; Privacy Policy</Button>
            </Col>
            <Col xs={12} sm lg={isLandingPage && 2} className="d-flex align-items-center justify-content-center">
              <Button variant="text" className={`${isLandingPage && 'link-color'}`} onClick={() => onButtonClick(links.Accessibility)}>Accessibility Policy</Button>
            </Col>
            {!isLandingPage && (
              <React.Fragment>
                <Col xs={12} sm className="d-flex align-items-center justify-content-center">
                  <Button variant="text" onClick={() => onButtonClick(links.Contact)}>Contact Us</Button>
                </Col>
                <Col xs={12} sm className="d-flex align-items-center justify-content-center lastCol">
                  <Row>
                    <span>Account ID: {accountId}</span>
                  </Row>
                  <Row>
                    <span>Version: {sentryBuildNumber}</span>
                  </Row>
                </Col>
              </React.Fragment>
            )}
            {isLandingPage && (
              <Col xs={12} sm lg={isLandingPage && 2} className="d-flex align-items-center justify-content-center">
                <span>Copyright &copy; {year}</span>
              </Col>
            )}
          </Row>
          <Row className="justify-content-center"><strong>Powered by</strong></Row>
          <Row className="justify-content-center">
            <img src={`${process.env.PUBLIC_URL}/logos/covr-logo.svg`} alt="Covr Logo" />
          </Row>
          <Row className="justify-content-center covr">Covr Financial Technologies</Row>
          <Row className="justify-content-center">Hartford. Boise. Colorado Springs.</Row>
          {!isLandingPage && <Row className="justify-content-center">Copyright &copy; {year}</Row>}
        </Col>

        <FooterModalPopup
          open={infoDialogOpen}
          onDialogClose={() => {
            setInfoDialogOpen(false);
          }}
          component={renderComponent}
        />

        <SupportModal open={modalOpen} handleClose={handleCloseModal}/>

      </Container>
    </footer>
  );
}



// WEBPACK FOOTER //
// ./src/components/Footer/Footer.js